import React from 'react';
import { SearchIcon, XIcon, ChevronRightIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { useAddressLookup } from '../../hooks/useAddressLookup';
import { ADDRESS_LOOKUP_COPY } from './copy';
import type { NormalisedAddress } from '../../types/addressLookup.types';
import { INPUT_BASE, INPUT_STATE } from '@/shared/components/form/input/inputStyles';

export interface AddressLookupProps {
    onAddressSelected: (address: NormalisedAddress) => void;
    onRetrievePendingChange?: (isPending: boolean) => void;
    id?: string;
    placeholder?: string;
    disabled?: boolean;
    minChars?: number;
}

export const AddressLookup: React.FC<AddressLookupProps> = ({
    onAddressSelected,
    onRetrievePendingChange,
    id,
    placeholder = 'Start typing an address or postcode',
    disabled = false,
    minChars = 3,
}) => {
    const {
        inputRef,
        query,
        isRetrieving,
        results,
        activeIndex,
        statusText,
        showClear,
        showListbox,
        listboxPlaceholder,
        setHighlighted,
        select,
        clear,
        handleChange,
        handleFocus,
        handleBlur,
        handleKeyDown,
    } = useAddressLookup({ onAddressSelected, disabled, minChars });

    // The awaited onAddressSelected runs before this flag clears, so a host gating
    // submit on it never re-enables while its form still holds the old address.
    React.useEffect(() => {
        onRetrievePendingChange?.(isRetrieving);
    }, [isRetrieving, onRetrievePendingChange]);

    const rid = React.useId();
    const listboxId = `${rid}-listbox`;
    const optionId = (idx: number) => `${rid}-opt-${idx}`;

    return (
        <div>
            <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2">
                    <SearchIcon className="text-mp-muted" size={21} />
                </span>
                <input
                    ref={inputRef}
                    id={id}
                    type="text"
                    role="combobox"
                    aria-label={id ? undefined : ADDRESS_LOOKUP_COPY.ariaLabel}
                    aria-autocomplete="list"
                    aria-expanded={showListbox}
                    aria-controls={showListbox ? listboxId : undefined}
                    aria-activedescendant={
                        showListbox && results.length ? optionId(activeIndex) : undefined
                    }
                    className={twMerge(
                        INPUT_BASE,
                        INPUT_STATE.default,
                        'pl-12',
                        showClear ? 'pr-10' : 'pr-4',
                        disabled && INPUT_STATE.disabled
                    )}
                    placeholder={placeholder}
                    value={query.text}
                    disabled={disabled}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                />
                {showClear && (
                    <button
                        type="button"
                        aria-label="Clear address search"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-mp-muted hover:text-mp-foreground"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={clear}
                    >
                        <XIcon size={18} />
                    </button>
                )}
                {showListbox && (
                    <ul
                        id={listboxId}
                        className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-mp-input bg-mp-surface shadow-lg"
                        role="listbox"
                    >
                        {results.length === 0 && listboxPlaceholder && (
                            <li className="px-3 py-2 text-sm text-mp-muted">
                                {listboxPlaceholder}
                            </li>
                        )}
                        {results.map((row, idx) => (
                            <li
                                key={row.id}
                                id={optionId(idx)}
                                role="option"
                                aria-selected={activeIndex === idx}
                                className={`flex items-center justify-between gap-2 px-3 py-2 cursor-pointer text-sm ${activeIndex === idx ? 'bg-mp-primary-content text-mp-link' : 'hover:bg-mp-background'}`}
                                onMouseDown={(e) => {
                                    // preventDefault keeps focus in the input so no blur
                                    // fires, which is what keeps a container drill-down stable.
                                    e.preventDefault();
                                    void select(row);
                                }}
                                onMouseEnter={() => setHighlighted(idx)}
                            >
                                <span>
                                    <span className="font-medium">{row.text}</span>{' '}
                                    <span className="text-xs text-mp-muted">
                                        {row.description}
                                    </span>
                                </span>
                                {row.type === 'container' && (
                                    <>
                                        <ChevronRightIcon size={16} aria-hidden="true" />
                                        <span className="sr-only">contains further addresses</span>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <p
                role="status"
                aria-live="polite"
                className="mt-1.5 text-xs text-mp-muted"
            >
                {statusText}
            </p>
        </div>
    );
};
